import { useCallback, useSyncExternalStore } from 'react';
import {
  getFavoriteOrgIds,
  subscribeToFavoriteOrgIds,
  toggleFavoriteOrg as toggleFavoriteOrgInSmartService,
} from '@pypestream/design-system';

const EMPTY_FAVORITE_ORG_IDS: string[] = [];

function getSnapshot(): string[] {
  const current = getFavoriteOrgIds();
  return current.length ? current : EMPTY_FAVORITE_ORG_IDS;
}

// `subscribeToFavoriteOrgIds` calls back with the VALUE (and fires once
// immediately); `useSyncExternalStore` needs a no-arg "something changed, go
// re-read `getSnapshot()`" subscriber — adapt one to the other here rather
// than widening design-system's own, more general, subscribe API.
function subscribe(onStoreChange: () => void): () => void {
  return subscribeToFavoriteOrgIds(() => onStoreChange());
}

/**
 * Reads the logged-in user's `favoriteOrgIds` live from design-system's shared
 * `smartService`, and a `toggleFavoriteOrg` that persists a toggle through the
 * exact same `toggleFavoriteOrg` design-system exports — the ONE write path
 * `universal-nav-org-selector.wc.ts` (the Org nav menu, PSTUDIO-3330 Unit 1)
 * also calls, so a star toggled on the Roles table here and a star toggled in
 * the nav menu are instantly consistent with each other: no reload, no second
 * mutation that could drift from the first.
 */
export function useFavoriteOrgIds(): {
  favoriteOrgIds: string[];
  toggleFavoriteOrg: (params: { orgId: string }) => void;
} {
  const favoriteOrgIds = useSyncExternalStore(subscribe, getSnapshot);

  const toggleFavoriteOrg = useCallback(({ orgId }: { orgId: string }) => {
    toggleFavoriteOrgInSmartService(orgId);
  }, []);

  return { favoriteOrgIds, toggleFavoriteOrg };
}
